import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Select,
  DatePicker,
  Typography,
  Space,
  message,
  Spin,
  Input,
} from 'antd';
import {
  PlusOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import CreateFinancialRecordModal from '../component/financial/CreateFinancialRecordModal';
import { FinancialService } from '../service/financial-service/financial.service';
import {
  FinancialRecordResponseDto,
  FinancialSummary,
  FinancialRecordType,
} from '../model/Financial.model';
import ExportableTable from '../component/common/ExportableTable';
import { exportFinancialRecordsToExcel } from '../utils/excelExport';
import { useIsAdmin } from '../hook/useRole';

const { Title } = Typography;
const { Option } = Select;

const Financial: React.FC = () => {
  const [records, setRecords] = useState<FinancialRecordResponseDto[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    dayjs().month() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const isAdmin = useIsAdmin();

  useEffect(() => {
    fetchRecords();
    fetchSummary();
  }, [selectedMonth, selectedYear]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const data = await FinancialService.getAllRecords(
        undefined,
        selectedMonth,
        selectedYear
      );
      setRecords(data);
    } catch (error) {
      console.error('Error fetching records:', error);
      message.error('Không thể tải danh sách bản ghi thu chi');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await FinancialService.getFinancialSummary(
        selectedMonth,
        selectedYear
      );
      setSummary(data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      message.error('Không thể tải tổng hợp thu chi');
    }
  };

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchRecords();
    fetchSummary();
    message.success('Tạo bản ghi thu chi thành công!');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const columns = [
    {
      title: 'Ngày',
      dataIndex: 'recordDate',
      key: 'recordDate',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format('DD')] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
            disabledDate={(current) => {
              // Chỉ cho phép chọn ngày trong tháng và năm đã chọn
              return (
                !current ||
                current.month() !== selectedMonth - 1 ||
                current.year() !== selectedYear
              );
            }}
            placeholder="Chọn ngày trong tháng"
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const recordDay = dayjs(record.recordDate).format('DD');
        return recordDay === value;
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Loại',
      dataIndex: 'recordType',
      key: 'recordType',
      width: 100,
      filters: [
        { text: 'Thu', value: 'INCOME' },
        { text: 'Chi', value: 'EXPENSE' },
      ],
      onFilter: (value: string, record: any) => record.recordType === value,
      render: (type: string) => (
        <span
          className={`px-2 py-1 rounded text-sm ${
            type === 'INCOME'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {type === 'INCOME' ? 'Thu' : 'Chi'}
        </span>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        record.amount.toString().includes(value),
      render: (amount: number, record: any) => (
        <span
          className={`font-semibold ${
            record.recordType === 'INCOME' ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm mô tả"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        (record.description || '').toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm người tạo"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        (record.createdBy || '').toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            showTime
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.toISOString()] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY HH:mm"
            disabledDate={(current) => {
              // Chỉ cho phép chọn ngày trong tháng và năm đã chọn
              return (
                !current ||
                current.month() !== selectedMonth - 1 ||
                current.year() !== selectedYear
              );
            }}
            placeholder="Chọn ngày giờ trong tháng"
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const recordDate = dayjs(record.createdAt).format('YYYY-MM-DD HH:mm');
        const searchDate = dayjs(value).format('YYYY-MM-DD HH:mm');
        return recordDate === searchDate;
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  const handleExport = async (
    selectedRecords: FinancialRecordResponseDto[]
  ) => {
    await exportFinancialRecordsToExcel(
      selectedRecords,
      selectedMonth,
      selectedYear
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Quản lý Thu Chi</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          size="large"
        >
          Thêm bản ghi
        </Button>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng Thu"
              value={summary?.totalIncome || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng Chi"
              value={summary?.totalExpense || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<ArrowDownOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Số Dư"
              value={summary?.balance || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
              valueStyle={{
                color: (summary?.balance || 0) >= 0 ? '#52c41a' : '#f5222d',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng Bản Ghi"
              value={records.length}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-6">
        <Space wrap>
          <span>Tháng:</span>
          <Select
            value={selectedMonth}
            style={{ width: 100 }}
            onChange={(value) => setSelectedMonth(value)}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <Option key={i + 1} value={i + 1}>
                {i + 1}
              </Option>
            ))}
          </Select>

          <span>Năm:</span>
          <Select
            value={selectedYear}
            style={{ width: 100 }}
            onChange={(value) => setSelectedYear(value)}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = dayjs().year() - 2 + i;
              return (
                <Option key={year} value={year}>
                  {year}
                </Option>
              );
            })}
          </Select>

          <Button onClick={fetchRecords}>Làm mới</Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Spin spinning={loading}>
          <ExportableTable
            key={`financial-${selectedMonth}-${selectedYear}-${records.length}`} // Force reset filters khi data thay đổi
            columns={columns}
            dataSource={records.sort(
              (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
            )}
            loading={loading}
            onExport={handleExport}
            exportTitle="Xuất báo cáo thu chi"
            isAdmin={isAdmin}
          />
        </Spin>
      </Card>

      {/* Create Modal */}
      <CreateFinancialRecordModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Financial;
